import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  PieChart, 
  RefreshCw, 
  Sparkles, 
  Copy, 
  Check, 
  ThumbsUp, 
  ThumbsDown,
  Loader2,
  Users,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bug 5 Fix: Use standard NurtureOS design tokens instead of custom palette
// Matches Dashboard, Household Tasks, Handoff Board styling

const LoadMapPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadData, setLoadData] = useState(null);
  const [coachAdvice, setCoachAdvice] = useState(null);
  const [loadingCoach, setLoadingCoach] = useState(false);
  const [coachCallId, setCoachCallId] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [rated, setRated] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null); // Bug 5: For showing member's tasks

  useEffect(() => {
    fetchLoadMap();
  }, []);

  const fetchLoadMap = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/load-map/calculate`);
      setLoadData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Load Map is only available to parents and partners');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const getCoachAdvice = async () => {
    if (!loadData) return;
    
    setLoadingCoach(true);
    setCoachAdvice(null);
    setRated(false);
    
    try {
      const response = await axios.post(`${API}/ai/load-map/coach`, {
        load_data: loadData
      });
      setCoachAdvice(response.data);
      setCoachCallId(response.data.call_id);
    } catch (error) {
      toast.error('Could not get advice right now');
    } finally {
      setLoadingCoach(false);
    }
  };

  const confirmAdvice = async (action) => {
    if (!coachCallId) return;
    
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: coachCallId,
        user_action: action
      });
      setRated(true);
      toast.success(action === 'accepted' ? 'Glad it helped!' : 'Thanks for the feedback');
    } catch (error) {
      console.error('Failed to confirm:', error);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Check role access
  if (user && !['parent', 'partner'].includes(user.family_role)) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-nurture-cream">
        <Card className="max-w-md rounded-3xl border-none shadow-nurture">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-nurture-terracotta" />
            <h2 className="text-xl font-heading mb-2 text-nurture-navy">
              Access Restricted
            </h2>
            <p className="text-nurture-muted">
              Load Map is only available to parents and partners.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6 p-4 bg-nurture-cream min-h-screen">
        <div className="h-12 rounded-xl animate-pulse bg-nurture-cream-dark" />
        <div className="h-64 rounded-xl animate-pulse bg-nurture-cream-dark" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="load-map-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center bg-pink-100">
              <PieChart className="w-5 h-5 sm:w-6 sm:h-6 text-nurture-terracotta" />
            </div>
            Your Family Load Map
          </h1>
          <p className="mt-1 text-nurture-muted">
            This week's household, by who's carrying it
          </p>
        </div>
        
        <Button
          onClick={fetchLoadMap}
          variant="outline"
          className="rounded-xl border-nurture-cream-dark text-nurture-navy"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Recalculate this week
        </Button>
      </div>

      {/* Chaos Meter - same number as the Dashboard */}
      {loadData && (
        <Card className="rounded-2xl border-nurture-cream-dark" data-testid="load-map-chaos-card">
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-nurture-navy">Family Chaos Meter</p>
              <p className="text-xs text-nurture-muted">
                {loadData.total_open} open this week of a healthy {loadData.threshold}
              </p>
            </div>
            <span className="text-2xl font-bold text-nurture-terracotta" data-testid="load-map-chaos-percentage">
              {loadData.over_capacity ? 'Over capacity' : `${loadData.chaos_percentage}%`}
            </span>
          </CardContent>
        </Card>
      )}

      {/* Overload Banner - only when shares are meaningful */}
      {loadData?.overload_flag && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card 
            className="rounded-2xl border-l-4 border-l-nurture-terracotta bg-orange-50 border-nurture-cream-dark"
            data-testid="overload-banner"
          >
            <CardContent className="p-4">
              <p className="text-nurture-navy">
                You're carrying <strong>{loadData.primary_parent_load}%</strong> of everything this week. 
                That's a lot for one person to hold. Here's where some of it could shift.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Unassigned Note */}
      {loadData?.unassigned_count > 0 && (
        <Card className="rounded-2xl bg-nurture-cream border-nurture-cream-dark">
          <CardContent className="p-4">
            <p className="text-nurture-muted" data-testid="unassigned-note">
              {loadData.unassigned_count} of these {loadData.unassigned_count === 1 ? 'item' : 'items'} {loadData.unassigned_count === 1 ? "doesn't" : "don't"} have an owner yet.
              Assign tasks to people on <button onClick={() => navigate('/tasks')} className="underline text-nurture-navy" data-testid="go-household-tasks">Household Tasks</button> to see who's carrying what.
              School items are tracked per child and aren't assigned to an adult yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Load Bars */}
      <Card className="rounded-2xl border-nurture-cream-dark">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-nurture-navy font-heading">
            <Users className="w-5 h-5 text-nurture-terracotta" />
            Task Distribution
            {loadData && (
              <Badge 
                className="ml-auto bg-nurture-cream text-nurture-navy cursor-pointer hover:bg-nurture-cream-dark transition-colors"
                onClick={() => navigate('/tasks')}
                data-testid="tasks-count-badge"
              >
                {loadData.total_tasks} open this week →
              </Badge>
            )}
          </CardTitle>
          {/* Percentage explanation */}
          <p className="text-xs text-nurture-muted mt-1">
            {loadData?.assigned_count > 0
              ? `Percentage shows each person's share of the ${loadData.assigned_count} assigned ${loadData.assigned_count === 1 ? 'item' : 'items'}. Unassigned items are listed separately.`
              : 'Nothing is assigned yet, so there are no personal shares to show.'}
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {loadData?.total_tasks === 0 ? (
            <div className="text-center py-8">
              <p className="text-nurture-muted">No open items for this week yet.</p>
            </div>
          ) : (
            <>
            {loadData?.per_member?.map((member, index) => (
              <div key={index} className="space-y-2" data-testid={`member-bar-${index}`}>
                {/* Member info row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-nurture-navy">
                      {member.member_name}
                    </span>
                    <Badge 
                      variant="outline" 
                      className="text-xs border-nurture-muted text-nurture-muted"
                    >
                      {member.role}
                    </Badge>
                  </div>
                  <span className={`font-bold ${member.role === 'parent' ? 'text-nurture-terracotta' : 'text-nurture-muted'}`}>
                    {member.load_percentage}%
                  </span>
                </div>
                
                {/* Progress bar */}
                <div className="h-4 rounded-full overflow-hidden bg-nurture-cream-dark">
                  {member.load_percentage > 0 ? (
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${member.load_percentage}%` }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className={`h-full rounded-full ${member.role === 'parent' ? 'bg-nurture-terracotta' : 'bg-nurture-sage'}`}
                    />
                  ) : (
                    <div className="h-full border-2 border-dashed rounded-full flex items-center justify-center border-nurture-muted">
                      <span className="text-xs text-nurture-muted">
                        nothing assigned yet
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Category pills */}
                {member.categories && Object.keys(member.categories).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {Object.entries(member.categories).map(([cat, count]) => (
                      <span 
                        key={cat}
                        className="text-xs px-2 py-1 rounded-full bg-nurture-cream text-nurture-navy"
                      >
                        {cat.charAt(0).toUpperCase() + cat.slice(1)} ×{count}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loadData?.unassigned_count > 0 && (
              <div className="space-y-2" data-testid="unassigned-row">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-nurture-muted">Unassigned</span>
                  <span className="font-bold text-nurture-muted">
                    {loadData.unassigned_count} {loadData.unassigned_count === 1 ? 'task' : 'tasks'}
                  </span>
                </div>
                <div className="h-6 rounded-full border-2 border-dashed border-nurture-muted flex items-center justify-center">
                  <span className="text-[10px] leading-none text-nurture-muted">nobody is holding these yet</span>
                </div>
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Bug 7: AI Coach Button - Made more prominent with card wrapper */}
      {loadData?.total_tasks > 0 && !coachAdvice && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2"
        >
          <Card className="rounded-2xl border-2 border-nurture-terracotta/30 bg-gradient-to-r from-nurture-cream to-orange-50">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-8 h-8 mx-auto mb-3 text-nurture-terracotta" />
              <h3 className="font-heading text-lg text-nurture-navy mb-2">
                Seeing an imbalance?
              </h3>
              <p className="text-sm text-nurture-muted mb-4">
                Get a gentle conversation starter to discuss task sharing with your partner.
              </p>
              <Button
                onClick={getCoachAdvice}
                disabled={loadingCoach}
                size="lg"
                className="rounded-xl px-8 py-3 text-white bg-nurture-terracotta hover:bg-nurture-terracotta-hover shadow-lg"
                data-testid="coach-btn"
              >
                {loadingCoach ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Thinking...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Ask the Fairness Coach
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Book Quote - moved after coach button */}
      <div className="text-center py-6">
        <p className="italic text-lg max-w-2xl mx-auto text-nurture-terracotta font-heading">
          "The invisible load isn't just what you do. It's everything you carry in your head — 
          the remembering, the tracking, the worrying."
        </p>
        <p className="mt-2 text-sm text-nurture-muted">
          — Breathe, Then Answer
        </p>
      </div>

      {/* Coach Advice Card */}
      {coachAdvice && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card 
            className="rounded-2xl border-nurture-terracotta"
            data-testid="coach-advice-card"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-nurture-navy font-heading">
                <Sparkles className="w-5 h-5 text-nurture-terracotta" />
                A gentle nudge
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Insight */}
              <p className="text-nurture-navy leading-relaxed">
                {coachAdvice.insight}
              </p>
              
              {/* Conversation starters */}
              {coachAdvice.conversation_starters?.length > 0 && (
                <div className="space-y-3 pt-4">
                  <p className="text-sm font-medium text-nurture-muted">
                    Something you could say tonight:
                  </p>
                  {coachAdvice.conversation_starters.map((starter, index) => (
                    <div 
                      key={index}
                      className="relative p-4 rounded-2xl bg-nurture-cream"
                    >
                      <p className="text-nurture-navy pr-10">
                        "{starter}"
                      </p>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={() => copyToClipboard(starter, index)}
                        data-testid={`copy-starter-${index}`}
                      >
                        {copiedIndex === index ? (
                          <Check className="w-4 h-4 text-nurture-sage" />
                        ) : (
                          <Copy className="w-4 h-4 text-nurture-muted" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Rating row */}
              {!rated ? (
                <div className="flex items-center justify-center gap-4 pt-4 border-t border-nurture-cream-dark">
                  <span className="text-sm text-nurture-muted">Was this helpful?</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-nurture-terracotta text-nurture-terracotta"
                    onClick={() => confirmAdvice('accepted')}
                    data-testid="rate-helpful"
                  >
                    <ThumbsUp className="w-4 h-4 mr-1" />
                    This helped
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-nurture-muted text-nurture-muted"
                    onClick={() => confirmAdvice('ignored')}
                    data-testid="rate-not-helpful"
                  >
                    <ThumbsDown className="w-4 h-4 mr-1" />
                    Not for me
                  </Button>
                </div>
              ) : (
                <div className="text-center pt-4 border-t border-nurture-cream-dark">
                  <p className="text-sm text-nurture-muted">
                    Thanks for your feedback ❤️
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Last calculated timestamp */}
      {loadData?.calculated_at && (
        <p className="text-center text-sm text-nurture-muted">
          Last calculated: {new Date(loadData.calculated_at).toLocaleString()} · 
          Week of {formatDate(loadData.week_start)} - {formatDate(loadData.week_end)}
        </p>
      )}
    </div>
  );
};

export default LoadMapPage;
